<script setup>
/**
 * 同一个 `correlation_id` 的每一步 attempt。就是一张表：第几步、哪个模型、什么状态、多久、多少 token。
 *
 * `chain === null` 是事实不是空数据：这次调用没经过路由链（直接 `createLLM().chat()`）。
 */
import { computed } from 'vue'
import { Badge, Table } from '@drama/ui'
import { useI18n } from 'vue-i18n'
import { stepIndex, stepName } from '../call-chain.js'
import { formatDuration, formatTokens, stepNumber } from '../llm-format.js'

const props = defineProps({
  /** @type {import('vue').PropType<import('../../../api/llm/index.js').CallChain>} */
  chain: { type: Object, default: null },
})

const emit = defineEmits(['open'])

const { t } = useI18n()

const columns = computed(() => [
  { key: 'step', label: '#', width: '70px' },
  { key: 'model', label: t('visualGameAdmin.llm.calls.table.model'), width: '210px', mono: true },
  { key: 'status', label: t('visualGameAdmin.llm.calls.table.status'), width: '100px' },
  { key: 'duration', label: t('visualGameAdmin.llm.calls.table.duration'), width: '90px', align: 'right', mono: true },
  { key: 'tokens', label: t('visualGameAdmin.llm.calls.table.tokens'), width: '140px', align: 'right', mono: true },
])

const rows = computed(() =>
  (props.chain?.attempts ?? []).map((attempt) => ({
    request_id: attempt.request_id,
    is_current: attempt.is_current,
    step: t('visualGameAdmin.llm.calls.detail.chain.step', { index: stepNumber(stepIndex(attempt)) }),
    model: stepName(attempt),
    status: attempt.status,
    // 失败原因是排障要的东西，挂在状态徽标的 title 上，不占一列。
    error_reason: attempt.error_reason,
    duration: formatDuration(attempt.duration_ms),
    tokens: formatTokens(attempt),
  })),
)

const STATUS_TEXT = {
  ok: 'answered',
  aborted: 'aborted',
  error: 'failed',
}
const STATUS_TONE = {
  ok: 'success',
  aborted: 'warning',
  error: 'danger',
}

/** @param {string} status */
function statusText(status) {
  const key = STATUS_TEXT[status]
  if (!key) throw new Error(`CallChain: 认不出的 status「${status}」`)
  return t(`visualGameAdmin.llm.calls.detail.chain.${key}`)
}
</script>

<template>
  <section
    class="call-chain"
    data-insp-path="apps/visual-game-admin/src/pages/llm/call/CallChain.vue#chain"
  >
    <h3>{{ t('visualGameAdmin.llm.calls.detail.chain.title') }}</h3>

    <p
      v-if="chain === null"
      class="call-chain__not-routed"
    >
      {{ t('visualGameAdmin.llm.calls.detail.chain.notRouted') }}
    </p>

    <Table
      v-else
      :columns="columns"
      :rows="rows"
      row-key="request_id"
    >
      <template #step="{ row }">
        <span :class="{ 'is-current': row.is_current }">{{ row.step }}</span>
      </template>
      <template #model="{ row }">
        <button
          class="call-chain__model"
          type="button"
          @click="emit('open', row.request_id)"
        >
          {{ row.model }}
        </button>
      </template>
      <template #status="{ row }">
        <Badge
          :variant="STATUS_TONE[row.status]"
          :title="row.error_reason || undefined"
        >
          {{ statusText(row.status) }}
        </Badge>
      </template>
    </Table>
  </section>
</template>

<style scoped>
.call-chain {
  display: grid;
  gap: var(--drama-space-md);
  min-width: 0;
}

h3 {
  margin: 0;
  font-family: var(--drama-font-family-heading);
  font-size: var(--drama-font-size-lg);
  font-weight: var(--drama-font-weight-medium);
}

.call-chain__not-routed {
  margin: 0;
  color: var(--drama-color-text-muted);
  font-size: var(--drama-font-size-sm);
}

.is-current {
  color: var(--drama-color-primary);
  font-weight: var(--drama-font-weight-medium);
}

.call-chain__model {
  padding: 0;
  border: none;
  background: none;
  color: var(--drama-color-text);
  font-family: var(--drama-font-family-mono);
  font-size: var(--drama-font-size-sm);
  text-align: left;
  text-decoration: underline;
  text-underline-offset: 3px;
  cursor: pointer;
}
</style>
